import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Zap, Loader2, Send, CheckCircle2, MessageCircleHeart } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function ReplyPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const load = () => api.get(`/reply/${token}`).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/reply/${token}`, { text: text.trim() });
      setText("");
      setSent(true);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="reply-page">
      <header className="bg-[#1B263B] text-white">
        <div className="max-w-xl mx-auto px-5 py-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#FDE68A]" />
          <span className="font-heading font-extrabold text-lg">VoltRoute</span>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-5 py-8">
        {error && (
          <div className="bg-card rounded-2xl p-6 text-center" data-testid="reply-error">
            <p className="font-heading font-bold mb-1">Oeps</p>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        )}
        {!error && !data && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}
        {data && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#B5838D] mb-1">Reactie op &lsquo;{data.trip_name}&rsquo;</p>
            <h1 className="font-heading font-bold text-xl mb-5">Hoi {data.recipient_name?.split(" ")[0]}, antwoord direct</h1>
            {data.deleted ? (
              <div className="bg-card rounded-2xl p-6 text-center" data-testid="reply-deleted">
                <p className="text-sm text-muted-foreground">Deze reactie is intussen verwijderd.</p>
              </div>
            ) : (
              <div className="bg-card rounded-2xl p-5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-3">
                  <MessageCircleHeart className="w-3.5 h-3.5 text-[#B5838D]" /> Het gesprek
                </p>
                <div className="space-y-2 mb-4">
                  {data.thread.map((c) => (
                    <div key={c.id} data-testid={`reply-thread-${c.id}`}
                      className={`bg-[#B5838D]/5 border border-[#B5838D]/10 rounded-xl px-3.5 py-2.5 ${c.parent_id ? "ml-7" : ""} ${c.id === data.comment_id ? "ring-1 ring-[#B5838D]/40" : ""}`}>
                      <p className="text-sm leading-relaxed">{c.text}</p>
                      <p className="text-xs text-muted-foreground font-semibold mt-1">
                        {c.author_name}
                        {c.is_owner && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5">reiziger</span>}
                        <span className="font-mono font-normal"> · {new Date(c.created_at).toLocaleString("nl-BE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      </p>
                    </div>
                  ))}
                </div>
                {sent && (
                  <p data-testid="reply-success" className="flex items-center gap-1.5 text-sm text-[#2D6A4F] font-semibold mb-3">
                    <CheckCircle2 className="w-4 h-4" /> Je antwoord staat op het reisverslag!
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <input data-testid="reply-input" value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()} maxLength={1000} placeholder="Typ je antwoord..."
                    className="flex-1 min-w-0 bg-[#B5838D]/10 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#B5838D] transition-[box-shadow] duration-200" />
                  <button data-testid="reply-send" onClick={send} disabled={!text.trim() || busy}
                    className="w-10 h-10 rounded-full bg-[#B5838D] hover:bg-[#8E5D67] text-white flex items-center justify-center disabled:opacity-40 shrink-0 active:scale-95 transition-[transform,background-color] duration-200">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Je antwoordt als <strong>{data.recipient_name}</strong> — zonder in te loggen.</p>
              </div>
            )}
            <div className="text-center mt-6">
              <Link to={`/trip/${data.trip_id}`} data-testid="reply-open-app" className="text-xs font-semibold text-primary hover:underline">
                Open het volledige reisverslag in VoltRoute →
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
